import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useEffect, useMemo } from "react";
import useEffectSkipFirst from "../../../hooks/useEffectSkipFirst";
import {
  weatherAutoRefreshWithID,
  weatherMissingWithID,
  weatherTimeWithID,
  weatherVisibleWithID
} from "../../../store/WeatherContentsState";
import { addMapLegendSelector, removeMapLegendSelector } from "../../../store/MapLegendState";
import WxRangeAltDispatcher from "./WxRangeAltDispatcher";
import WxIndividualAltDispatcher from "./WxIndividualAltDispatcher";
import { elapsedTimeControllerEnable, elapsedTimeForWeatherState } from "../../../store/ElapsedTimeState";
import { isEmpty, parseUTCDate } from "../../../utils/FPEUtils";
import { useATCPlan } from "../../../store/ATCPlanState";
import {
  addDrawCoastLineOverContentsState,
  removeDrawCoastLineOverContentsState
} from "../../../store/DrawCoastLineOverContentsState";
import {
  addMapTimeControllerSelector,
  mapAutoRefreshState,
  removeMapTimeControllerSelector,
  unificationMapTimeControllerState
} from "../../../store/MapTimeControllerState";
import { useExecutePreMount } from "../../../hooks/useExecutePreMount";
import { useAlert } from "../../../hooks/useAlert";
import { removeDuplicateMessagesSelector } from "../../../store/AlertState";
import { makeErrorMessage } from "../../../utils/ErrorMessageUtil";

function WxContentsDispatcher({contentsID, contents, altType}) {
  const {showError, showSuccess, removeAlert} = useAlert();
  const dispatcher = useRecoilCallback(({set}) => action => {
    if (action.type === 'SET_CONTENTS_TIME') {
      const contentsID = action.payload.id;
      set(weatherTimeWithID(contentsID), action.payload);
    } else if (action.type === 'SET_CONTENTS_MISSING') {
      const {id, missing} = action.payload;
      set(weatherMissingWithID(id), missing);
    } else if (action.type === 'SHOW_ERROR') {
      const errorMessage = makeErrorMessage(action.payload);
      set(removeDuplicateMessagesSelector, errorMessage);
      return showError(errorMessage);
    } else if (action.type === 'CLEAR_ERROR') {
      if (action.payload) {
        removeAlert(action.payload);
      }
    } else if (action.type === 'SHOW_TIPS') {
      const message = action.payload;
      return showSuccess(message, {timeout: 2000, close: false});
    }
  });

  // 後続処理のweatherVisibleWithIDの中でコンテンツ初期描画化が行われ、dispatcherにアクセスがあるのでweatherVisibleWithIDより前に記述する必要がある
  useExecutePreMount(() => {
    contents.setDispatcher(dispatcher);
  });

  const [visible, setVisible] = useRecoilState(weatherVisibleWithID(contentsID));
  //統一タイムコントローラ
  const unificationTimeController = useRecoilValue(unificationMapTimeControllerState);
  //自動更新（全体）
  const unificationAutoRefresh  = useRecoilValue(mapAutoRefreshState);
  //自動更新（コンテンツ個別）
  const [autoRefresh, setAutoRefresh] = useRecoilState(weatherAutoRefreshWithID(contentsID));
  const addMapLegend = useSetRecoilState(addMapLegendSelector);
  const removeMapLegend = useSetRecoilState(removeMapLegendSelector);
  const elapsedTimeForWeather = useRecoilValue(elapsedTimeForWeatherState);
  const {atcPlan} = useATCPlan();
  const addCoastLineState = useSetRecoilState(addDrawCoastLineOverContentsState);
  const removeCoastLineState = useSetRecoilState(removeDrawCoastLineOverContentsState);
  const addMapTimeController = useSetRecoilState(addMapTimeControllerSelector);
  const removeMapTimeController = useSetRecoilState(removeMapTimeControllerSelector);
  const routeElapsedTimeEnable = useRecoilValue(elapsedTimeControllerEnable);

  const visibleDependency = useMemo(() => ([visible]), [visible]);

  useEffect(() => {
    if(visible) {
      if (typeof contents.getTimeController === 'function') {
        const timeController = contents.getTimeController();
        addMapTimeController(timeController);
      }
      contents.drawLayer(visible);
      //selectedWeatherListを更新するため
      setVisible(visible);
      if (typeof contents.getLegend === 'function') {
        addMapLegend(contents.getLegend());
      }
      if (typeof contents.isDrawCoastLineOverContents == 'function') {
        if(contents.isDrawCoastLineOverContents()) {
          addCoastLineState(contentsID);
        }
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffectSkipFirst(async() => {
    if (typeof contents.getTimeController === 'function') {
      if (visible) {
        //自動更新（全体）のステートを適用させる
        const timeController = contents.getTimeController();
        timeController.changeAutoRefresh(unificationAutoRefresh);
        setAutoRefresh(unificationAutoRefresh);

        //統一タイムコントローラの選択時制を適用させる
        timeController.setCurrentTime(unificationTimeController.getCurrentTime());
        addMapTimeController(timeController);
      } else {
        removeMapTimeController(contents.getTimeController());
      }
    }

    await contents.drawLayer(visible);

    if (typeof contents.getLegend === 'function') {
      if (visible && contents.visible) {
        addMapLegend(contents.getLegend());
      } else {
        removeMapLegend(contents.getLegend());
      }
    }
    if (typeof contents.isDrawCoastLineOverContents == 'function') {
      if (visible && contents.isDrawCoastLineOverContents()) {
        addCoastLineState(contentsID);
      } else {
        removeCoastLineState(contentsID);
      }
    }
  }, visibleDependency);

  useEffect(()=> {
    if (!routeElapsedTimeEnable) {
      return;
    }
    const json = atcPlan.edit.json;
    let etd = null;
    if(!isEmpty(json)) {
      if(json.dof) {
        etd = parseUTCDate(`${json.dof} ${json.dep.etd}00`);
      } else {
        etd = new Date();
        const depTime = parseInt(json.dep.etd.substr(0, 2));
        const depMin = parseInt(json.dep.etd.substr(2));
        if(depTime < etd.getUTCHours() || (depTime === etd.getUTCHours() && depMin < etd.getUTCMinutes())) {
          etd.setUTCDate(etd.getUTCDate() + 1);
        }
        etd.setUTCHours(depTime, depMin, 0);
      }
      contents.applyPlanETD && contents.applyPlanETD(etd);
    }
  }, [atcPlan.edit.json, contents, routeElapsedTimeEnable]);

  useEffect(()=>{
    contents.applyElapsed && contents.applyElapsed(elapsedTimeForWeather);
  }, [elapsedTimeForWeather, contents])

  useEffect(() => {
    contents.setAutoRefresh && contents.setAutoRefresh(autoRefresh);
  }, [autoRefresh, contents]);

  return (
    <>
      {altType === 'range' && <WxRangeAltDispatcher contents={contents}/>}
      {altType === 'individual' && <WxIndividualAltDispatcher contentsID={contentsID} contents={contents}/>}
    </>
  );
}

export default WxContentsDispatcher